import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enTranslation from '../locales/en/translation.json';
import bnTranslation from '../locales/bn/translation.json';
import kmTranslation from '../locales/km/translation.json';
import loTranslation from '../locales/lo/translation.json';
import swTranslation from '../locales/sw/translation.json';
import amTranslation from '../locales/am/translation.json';
import viTranslation from '../locales/vi/translation.json';
import tlTranslation from '../locales/tl/translation.json';
import thTranslation from '../locales/th/translation.json';
import urTranslation from '../locales/ur/translation.json';
// FIX-23: AM/AZ/UZ cast languages
import hyTranslation from '../locales/hy/translation.json';
import azTranslation from '../locales/az/translation.json';
import uzTranslation from '../locales/uz/translation.json';

export const resources = {
  en: { translation: enTranslation },
  bn: { translation: bnTranslation },
  km: { translation: kmTranslation },
  lo: { translation: loTranslation },
  sw: { translation: swTranslation },
  am: { translation: amTranslation },
  vi: { translation: viTranslation },
  tl: { translation: tlTranslation },
  th: { translation: thTranslation },
  ur: { translation: urTranslation },
  hy: { translation: hyTranslation },
  az: { translation: azTranslation },
  uz: { translation: uzTranslation },
} as const;

// Normalize raw language codes (from DB or storage) into codes registered in `resources`.
// DB historically stored 'la' for Laos users; the registered resource is 'lo' (ISO-639-1 Lao).
export function normalizeLanguageCode(raw: string | null | undefined): string {
  const code = (raw ?? '').trim().toLowerCase();
  const aliases: Record<string, string> = {
    la: 'lo',
  };
  return aliases[code] ?? code;
}

// All supported languages with metadata
export const languages = [
  { code: 'en', name: 'English', nativeName: 'English', fontFamily: 'font-sans', locale: 'en-US' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', fontFamily: 'font-bengali', locale: 'bn-BD' },
  { code: 'km', name: 'Khmer', nativeName: 'ខ្មែរ', fontFamily: 'font-khmer', locale: 'km-KH' },
  { code: 'lo', name: 'Lao', nativeName: 'ລາວ', fontFamily: 'font-lao', locale: 'lo-LA' },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', fontFamily: 'font-sans', locale: 'sw-KE' },
  { code: 'am', name: 'Amharic', nativeName: 'አማርኛ', fontFamily: 'font-sans', locale: 'am-ET' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', fontFamily: 'font-sans', locale: 'vi-VN' },
  { code: 'tl', name: 'Filipino', nativeName: 'Filipino', fontFamily: 'font-sans', locale: 'tl-PH' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', fontFamily: 'font-thai', locale: 'th-TH' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', fontFamily: 'font-urdu', locale: 'ur-PK', rtl: true },
  // FIX-23: AM/AZ/UZ cast languages (hy needs Armenian glyph coverage; az/uz are Latin)
  { code: 'hy', name: 'Armenian', nativeName: 'Հայերեն', fontFamily: 'font-armenian', locale: 'hy-AM' },
  { code: 'az', name: 'Azerbaijani', nativeName: 'Azərbaycanca', fontFamily: 'font-sans', locale: 'az-AZ' },
  { code: 'uz', name: 'Uzbek', nativeName: 'Oʻzbekcha', fontFamily: 'font-sans', locale: 'uz-UZ' },
];

// Map country codes to their local language code
export const countryToLanguage: Record<string, string> = {
  BD: 'bn',
  KH: 'km',
  LA: 'lo',
  KE: 'sw',
  UG: 'sw',
  ET: 'am',
  VN: 'vi',
  PH: 'tl',
  TH: 'th',
  PK: 'ur',
  // FIX-23: cast countries
  AM: 'hy',
  AZ: 'az',
  UZ: 'uz',
};

// RTL languages
export const rtlLanguages = ['ur', 'ar'];

// Check if a language code is RTL
export const isRTL = (languageCode: string): boolean => {
  return rtlLanguages.includes(languageCode);
};

// Get the two language options for a given country: English + local language
export const getLanguagesForCountry = (countryCode: string) => {
  const english = languages.find(l => l.code === 'en')!;
  const localLangCode = countryToLanguage[countryCode];
  const localLang = localLangCode ? languages.find(l => l.code === localLangCode) : null;
  
  // Always return English; add local language if it exists and isn't English
  if (localLang && localLang.code !== 'en') {
    return [english, localLang];
  }
  return [english];
};

export const getLanguageFont = (languageCode: string): string => {
  const language = languages.find(lang => lang.code === languageCode);
  return language?.fontFamily || 'font-sans';
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: import.meta.env.DEV,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

export default i18n;
